/// <amd-module name="VSSUI/Components/PersonaCard/PersonaCard" />
/**
 * The PersonaCard is intended to show contact and organization information for an identity.
 * You may pass the entity directly or you may pass a unique attribute (e.g. uniqueName, entityID, signInAddress) as a prop.
 *
 */

import * as React from "react";
import { CardType, IDataState, PersonaCardProps, IIdentity, IPersonaConnections } from "./PersonaCard.Props";
import * as Utils from "./PersonaCardIdentityUtils";
import { PersonaCardContent } from "./PersonaCardContent";

/** @deprecated */
export interface PersonaCardState {
    /** Mimics stack to allow for breadcrumb */
    dataState: IDataState;

    /** Prevent requests if working */
    working: boolean;
}

/** @deprecated Please use import { IdentityCard } from "VSSUI/IdentityCard" */
export class PersonaCard extends React.Component<PersonaCardProps, PersonaCardState> {
    // used to stop processing callbacks if the card is dismissed before data loads
    private _dismissed: boolean;

    constructor(props: PersonaCardProps) {
        super(props);

        // Setup state data and history
        let initialDataState: IDataState = {
            identity: props.identity,
            managerList: null,
            directReportList: null,
            previousDataState: null,
            cardType: CardType.Default,
            header: props.initialHeader ? props.initialHeader.identity : null,
            displayName: props.displayName,
            imageUrl: props.imageUrl,
            email: this._getEmail()
        };
        let moreWorkNeeded = true;

        if (!props.identity && !props.uniqueAttribute && props.displayName) {
            // All we have is displayName and may be imageUrl. Simply go ahead and setup the state for displaying the card without any identity call.
            initialDataState.identity = {
                entityId: "",
                entityType: "user",
                originDirectory: "vsd",
                originId: "",
                displayName: props.displayName,
                image: props.imageUrl,
                signInAddress: initialDataState.email
            };
            moreWorkNeeded = false;
        }

        this.state = {
            dataState: initialDataState,
            working: moreWorkNeeded
        };
    }

    public componentDidMount(): void {
        if (this.state.working) {
            this._setupInitialData(this.props.uniqueAttribute);
        }
    }

    public componentWillUnmount(): void {
        this._dismissed = true;
    }

    //Render
    public render(): JSX.Element {
        return this.props.uniqueAttribute || this.props.identity || this.props.displayName ? (
            <PersonaCardContent
                {...this.props}
                dataProps={this.state.dataState}
                onClickEntity={this._onClickEntity}
                onDismissCallback={this._onDismissCallback}
                onShowContactCard={this._onShowContactCard}
                onShowOrganizationCard={this._onShowOrganizationCard}
                working={this.state.working}
                onHeaderClick={this._headerOnClickHandler}
            />
        ) : null;
    }

    private _onDismissCallback = (): void => {
        this.props.onDismissCallback();
    };

    // Handle going back
    private _headerOnClickHandler = (): void => {
        if (this.state.dataState.previousDataState) {
            this.setState({
                dataState: this.state.dataState.previousDataState,
                working: false
            });
        } else if (this.props.initialHeader && this.props.initialHeader.onClickFunction) {
            this.props.initialHeader.onClickFunction();
        }
    };

    private _onShowContactCard = (): void => {
        const newDataState: IDataState = {
            ...this.state.dataState,
            previousDataState: this.state.dataState,
            cardType: CardType.Contact,
            header: this.state.dataState.identity
        };
        this.setState({
            dataState: newDataState
        });
    };

    private _onShowOrganizationCard = (): void => {
        // Do not handle click event if working
        if (this.state.working || this._dismissed) {
            return;
        }
        const currentDataState = this.state.dataState;

        if (currentDataState.managerList && currentDataState.managerList.length <= 1) {
            // Only make the call if we don't have the data already
            const currentDataState = this.state.dataState;
            const newDataState: IDataState = {
                ...currentDataState,
                cardType: CardType.Organization,
                header: currentDataState.identity,
                previousDataState: currentDataState
            };
            this.setState({
                dataState: newDataState,
                working: true
            });

            this._resolveIdentity(newDataState, this.props.onRequestConnectionInformation(currentDataState.identity, true));
        } else {
            // Already have the data so we can present
            const currentDataState = this.state.dataState;
            const newDataState: IDataState = {
                ...currentDataState,
                cardType: CardType.Organization,
                header: currentDataState.identity,
                previousDataState: currentDataState
            };
            this.setState({
                dataState: newDataState,
                working: false
            });
        }
    };

    // Handle entity click
    private _onClickEntity = (identity: IIdentity): void => {
        // Do not handle click event if working
        if (this.state.working) {
            return;
        }

        const currentDataState = this.state.dataState;
        const newDataState: IDataState = {
            identity: identity,
            cardType: CardType.Default,
            header: currentDataState.identity,
            directReportList: [],
            managerList: [],
            previousDataState: currentDataState,
            displayName: identity.displayName,
            imageUrl: identity.image,
            email: identity.mail
        };
        this.setState({
            dataState: newDataState,
            working: true
        });

        // API call to get identity
        this._getIdentityByUniqueAttribute(identity);
    };

    // Helper method to get initial data (which includes only direct manager)
    private _setupInitialData(uniqueAttribute?: string): void {
        let dataState = this.state.dataState;
        if (!dataState.identity) {
            // Get identity first, then get connections in callback
            this._getIdentityByUniqueAttribute(uniqueAttribute);
        } else if (!Utils.isCompleteIdentity(dataState.identity)) {
            // Seems to be cached, refetch identity by unique attribute (entityId)
            this._getIdentityByUniqueAttribute(dataState.identity);
        } else {
            this._resolveIdentity(dataState, this.props.onRequestConnectionInformation(dataState.identity));
        }
    }

    // Helper method to get identity information given an entity ID
    private _resolveIdentity(dataState: IDataState, identity: IPersonaConnections | PromiseLike<IPersonaConnections>): void {
        const identityAsEntity: IPersonaConnections = identity as IPersonaConnections;
        const identityAsPromiseLike: PromiseLike<IPersonaConnections> = identity as PromiseLike<IPersonaConnections>;
        if (identityAsPromiseLike && identityAsPromiseLike.then) {
            identityAsPromiseLike.then((connections: IPersonaConnections) => {
                this._updateConnections(dataState, connections);
            });
        } else if (identityAsEntity) {
            this._updateConnections(dataState, identityAsEntity);
        }
    }

    // Helper method to get identity information given an entity ID
    private _resolveIEntity(identity: IIdentity | PromiseLike<IIdentity>): void {
        const identityAsEntity: IIdentity = identity as IIdentity;
        const identityAsPromiseLike: PromiseLike<IIdentity> = identity as PromiseLike<IIdentity>;
        if (identityAsPromiseLike && identityAsPromiseLike.then) {
            identityAsPromiseLike.then((connections: IIdentity) => {
                this._updateEntity(connections);
            });
        } else if (identityAsEntity) {
            this._updateEntity(identityAsEntity);
        }
    }

    private _updateConnections = (dataState: IDataState, connections: IPersonaConnections): void => {
        // Don't call if the component is unmounted.
        if (this._dismissed) {
            return;
        }
        // ensure we haven't attempted to update which card we were in before the callback happens.
        if (this.state.dataState && dataState.identity === this.state.dataState.identity) {
            this.setState({
                dataState: {
                    ...this.state.dataState,
                    managerList: connections.managers && connections.managers.reverse(),
                    directReportList: connections.directReports
                },
                working: false
            });
        }
    };

    private _updateEntity = (identity: IIdentity): void => {
        const hasCompleteIdentity = Utils.isCompleteIdentity(identity, true);

        const requireConnections = Utils.isAadUser(identity) || Utils.isAdUser(identity);
        const imageUrl: string = this.props.imageUrl ? this.props.imageUrl : identity.image;

        // Update data state and working state (visible state updated later)
        const newDataState: IDataState = {
            ...this.state.dataState,
            identity: identity,
            displayName: identity.displayName,
            imageUrl: imageUrl
        };
        // Check for authenticated users. For authenticated users, go on to make the connections call. For non-authenticated users, stop the calls and load the card with images.
        if (!hasCompleteIdentity) {
            this.setState({
                working: true
            });
        } else if (requireConnections) {
            this.setState({ dataState: newDataState, working: false });
            this._resolveIdentity(newDataState, this.props.onRequestConnectionInformation(newDataState.identity));
        } else {
            this.setState({ dataState: newDataState, working: false });
        }
    };

    // Helper method to get identity information given an entity ID
    private _getIdentityByUniqueAttribute(identifier: string | IIdentity): void {
        let uniqueAttribute: string;
        if (typeof identifier == "string") {
            uniqueAttribute = identifier;
        } else if (Utils.isAadUser(identifier) || Utils.isAdUser(identifier)) {
            uniqueAttribute = identifier.entityId;
        } else {
            uniqueAttribute = identifier.signInAddress; // VSD users (MSA accounts)
        }

        this._resolveIEntity(this.props.getEntityFromUniqueAttribute(uniqueAttribute));
    }

    private _getEmail(): string {
        // This is for displaying a fall back card when identities search doesn't fetch any identity
        if (Utils.isAadUser(this.props.identity) || Utils.isAdUser(this.props.identity)) {
            return this.props.identity.mail;
        } else if (this.props.identity && this.props.identity.signInAddress) {
            return this.props.identity.signInAddress;
        } else if (this.props.identity && this.props.identity.mail) {
            return this.props.identity.mail;
        } else if (this.props.uniqueAttribute) {
            // Check if uniqueAttribute is an email
            const parts = this.props.uniqueAttribute.split("@");
            if (parts.length === 2 && parts[0].length >= 1 && parts[1].length >= 3) {
                const domainParts = parts[1].split(".");
                if (domainParts.length > 1) {
                    return this.props.uniqueAttribute;
                }
            }
        }

        return null;
    }
}
